/**
 * "Buy with other shops" — scheduling a product into a group buy.
 *
 * The dialog has to answer three questions in the order a shopkeeper asks them:
 * *when* will I get it, *what* might it cost, and *what if not enough shops
 * join*. The third is the one that decides whether they press the button, so it
 * is a visible choice rather than a footnote — §4.2 asks for a refund or an
 * alternative offer, and this is the merchant making that choice up front
 * instead of being telephoned about it later.
 *
 * The dates come from the server, already narrowed by the wholesaler's cut-off
 * and buying days. Generating "the next seven days" here would offer dates the
 * very next request refuses, which reads as a broken app rather than as a
 * deadline.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";

import { ApiError } from "@/api/client";
import { Button, Dialog } from "@/components/ui";
import { cn } from "@/lib/cn";
import { formatDate, formatMoney } from "@/lib/format";
import {
  marketplaceRepository,
  type Listing,
  type PoolFallback,
} from "@/repositories/marketplaceRepository";

export interface ScheduleDialogProps {
  listing: Listing | null;
  onClose: () => void;
}

export function ScheduleDialog({ listing, onClose }: ScheduleDialogProps) {
  const client = useQueryClient();
  const [day, setDay] = useState<string>("");
  const [quantity, setQuantity] = useState(1);
  const [fallback, setFallback] = useState<PoolFallback>("BUY_ANYWAY");
  const [error, setError] = useState("");

  const detail = useQuery({
    queryKey: ["marketplace", "product", listing?.id],
    queryFn: () => marketplaceRepository.product(listing!.id),
    enabled: listing !== null,
  });

  useEffect(() => {
    if (listing) {
      setQuantity(listing.minimum_order_quantity);
      setFallback("BUY_ANYWAY");
      setError("");
    }
  }, [listing]);

  useEffect(() => {
    const first = detail.data?.schedulable_dates[0];
    if (first) setDay(first);
  }, [detail.data]);

  const schedule = useMutation({
    mutationFn: () =>
      marketplaceRepository.addToBasket({
        listing_id: listing!.id,
        quantity,
        scheduled_for: day,
        pool_fallback: fallback,
      }),
    onSuccess: (basket) => {
      client.setQueryData(["marketplace", "basket"], basket);
      onClose();
    },
    onError: (failure) =>
      setError(
        failure instanceof ApiError ? failure.detail : "Something went wrong.",
      ),
  });

  if (!listing) return null;

  const tiers = detail.data?.tiers ?? [];
  const best = tiers.at(-1);

  return (
    <Dialog
      open
      onClose={onClose}
      title="Buy with other shops"
      description={
        `${listing.name} costs ${formatMoney(listing.price)} on its own. ` +
        "Pick a day and it is bought together with every other shop ordering the " +
        "same thing that day — the more shops join, the lower the price for all of them."
      }
      footer={
        <>
          <Button
            variant="ghost"
            onClick={onClose}
            disabled={schedule.isPending}
          >
            Cancel
          </Button>
          <Button
            variant="money"
            loading={schedule.isPending}
            disabled={!day}
            onClick={() => schedule.mutate()}
          >
            Add to order
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {best && (
          <div className="rounded-control bg-money-50 p-3 text-sm text-money-700">
            As low as {formatMoney(best.unit_price)} each once{" "}
            {best.minimum_quantity} are bought altogether.
          </div>
        )}

        <fieldset>
          <legend className="mb-2 text-sm font-medium text-ink-900">
            Which day
          </legend>
          <div className="flex flex-wrap gap-2">
            {(detail.data?.schedulable_dates ?? []).map((candidate) => (
              <button
                key={candidate}
                type="button"
                onClick={() => setDay(candidate)}
                aria-pressed={candidate === day}
                className={cn(
                  "h-touch rounded-control border px-3 text-sm",
                  candidate === day
                    ? "border-brand-500 bg-brand-50 text-brand-600"
                    : "border-line bg-surface text-ink-600",
                )}
              >
                {formatDate(candidate)}
              </button>
            ))}
          </div>
        </fieldset>

        <label className="block text-sm">
          <span className="mb-1 block font-medium text-ink-900">How many</span>
          <input
            type="number"
            min={listing.minimum_order_quantity}
            step={listing.order_increment}
            value={quantity}
            onChange={(event) => setQuantity(Number(event.target.value))}
            className="h-touch w-full rounded-control border border-line px-3 text-sm"
          />
        </label>

        <fieldset>
          <legend className="mb-2 text-sm font-medium text-ink-900">
            If not enough shops join
          </legend>
          <div className="space-y-2">
            <Choice
              active={fallback === "BUY_ANYWAY"}
              onClick={() => setFallback("BUY_ANYWAY")}
              title="Order it anyway"
              detail={`At the normal ${formatMoney(listing.price)}. Best for stock you cannot run out of.`}
            />
            <Choice
              active={fallback === "RELEASE"}
              onClick={() => setFallback("RELEASE")}
              title="Put it back in my order"
              detail="Nothing is bought and you decide again. Best for stock that can wait."
            />
          </div>
        </fieldset>

        {error && (
          <p role="alert" className="text-sm text-danger-700">
            {error}
          </p>
        )}

        <p className="text-xs text-ink-500">
          You will never pay more than {formatMoney(listing.price)} each. A
          group buy can only bring the price down.
        </p>
      </div>
    </Dialog>
  );
}

function Choice({
  active,
  onClick,
  title,
  detail,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  detail: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "w-full rounded-control border p-3 text-left",
        active ? "border-brand-500 bg-brand-50" : "border-line bg-surface",
      )}
    >
      <span className="block text-sm font-medium text-ink-900">{title}</span>
      <span className="block text-xs text-ink-500">{detail}</span>
    </button>
  );
}
